import { useId } from 'react';

import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

export function AreaSparkline({
  values,
  xLabels,
  stat,
  delta,
  deltaUp,
  className,
}: {
  values: number[];
  xLabels: string[];
  stat: string;
  delta?: string;
  deltaUp?: boolean;
  className?: string;
}) {
  const gradId = useId();
  const W = 320, H = 120;
  const max = Math.max(...values, 1) * 1.15;
  const min = Math.min(...values, 0) * 0.85;
  const span = values.length > 1 ? W / (values.length - 1) : W;
  const y = (v: number) => H - ((v - min) / (max - min || 1)) * (H - 12) - 6;
  const points = values.map((v, i) => [i * span, y(v)] as const);
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${W} ${H} L 0 ${H} Z`;
  const DeltaIcon = deltaUp ? ArrowUpRight : ArrowDownRight;

  return (
    <div className={className}>
      <div className="mb-2.5 flex items-baseline gap-3">
        <span className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">{stat}</span>
        {delta && (
          <span className={`inline-flex items-center gap-0.5 text-body-sm font-medium ${deltaUp ? 'text-success-text' : 'text-danger-text'}`}>
            <DeltaIcon className="h-3.5 w-3.5" />
            {delta}
          </span>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-[130px] w-full text-primary" role="img">
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.18} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradId})`} />
        <path d={line} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="mt-1.5 flex justify-between">
        {xLabels.map((x, i) => (
          <span key={i} className="font-mono text-data-xs tabular-nums text-on-surface-subtle">{x}</span>
        ))}
      </div>
    </div>
  );
}
